import { useState } from 'react';
import { Comment } from '@/types/post';
import { usePosts } from '@/contexts/PostsContext';
import { useAdmin } from '@/contexts/AdminContext';
import { useLanguage } from '@/contexts/LanguageContext';

interface CommentSectionProps {
  postId: string;
  comments: Comment[];
  commentsEnabled: boolean;
}

const DeleteConfirmModal = ({
  onConfirm,
  onCancel,
  t,
}: {
  onConfirm: () => void;
  onCancel: () => void;
  t: (en: string, ko: string) => string;
}) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center">
    <div className="absolute inset-0 bg-black/30" onClick={onCancel} />
    <div className="relative bg-white rounded-xl shadow-lg p-5 w-80 max-w-[90vw]">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-red-50">
          <i className="ri-alert-line text-red-500 text-sm" />
        </div>
        <h3 className="text-sm font-semibold text-stone-800">
          {t('Delete comment?', '댓글을 삭제할까요?')}
        </h3>
      </div>
      <p className="text-xs text-stone-500 mb-4 leading-relaxed">
        {t(
          'This action cannot be undone. The comment will be permanently removed.',
          '이 작업은 되돌릴 수 없습니다. 댓글이 영구적으로 삭제됩니다.'
        )}
      </p>
      <div className="flex gap-2 justify-end">
        <button
          onClick={onCancel}
          className="text-xs px-3 py-1.5 rounded-lg border border-stone-200 text-stone-600 hover:bg-stone-50 cursor-pointer transition-colors whitespace-nowrap"
        >
          {t('Cancel', '취소')}
        </button>
        <button
          onClick={onConfirm}
          className="text-xs px-3 py-1.5 rounded-lg bg-red-500 text-white hover:bg-red-600 cursor-pointer transition-colors whitespace-nowrap"
        >
          {t('Delete', '삭제')}
        </button>
      </div>
    </div>
  </div>
);

const CommentSection = ({ postId, comments, commentsEnabled }: CommentSectionProps) => {
  const { addComment, deleteComment, blockComment, editComment } = usePosts();
  const { user, isAdmin } = useAdmin();
  const { t } = useLanguage();
  const [author, setAuthor] = useState('');
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');

  const isLoggedIn = !!user;
  const visibleComments = isAdmin ? comments : comments.filter((c) => !c.blocked);

  const canModifyComment = (c: Comment) => {
    if (isAdmin) return true;
    if (isLoggedIn && c.userId && c.userId === user?.id) return true;
    return false;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const displayName = isLoggedIn ? (user.displayName || user.email.split('@')[0]) : author.trim();
    if (!displayName || !text.trim()) {
      setError(t('Please fill in all fields.', '이름과 내용을 모두 입력해주세요.'));
      return;
    }
    if (text.length > 300) {
      setError(t('Comment must be under 300 characters.', '댓글은 300자 이내로 작성해주세요.'));
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await addComment(postId, displayName, text.trim(), user?.id || null);
      if (!isLoggedIn) setAuthor('');
      setText('');
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 3000);
    } catch {
      setError(t('Failed to post comment.', '댓글 등록에 실패했습니다.'));
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (c: Comment) => {
    setEditingId(c.id);
    setEditText(c.text);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  const saveEdit = async (commentId: string) => {
    if (!editText.trim()) return;
    if (editText.length > 300) {
      setError(t('Comment must be under 300 characters.', '댓글은 300자 이내로 작성해주세요.'));
      return;
    }
    await editComment(postId, commentId, editText.trim());
    setEditingId(null);
    setEditText('');
    setError('');
  };

  const handleDelete = async (commentId: string) => {
    await deleteComment(postId, commentId);
    setDeleteTarget(null);
  };

  const handleBlock = async (commentId: string, currentlyBlocked: boolean) => {
    await blockComment(postId, commentId, !currentlyBlocked);
  };

  return (
    <div className="mt-2">
      <h4 className="text-sm font-semibold text-stone-700 mb-4 tracking-wide uppercase">
        {t('Comments', '댓글')} ({visibleComments.length})
      </h4>

      {/* Comments list */}
      <div className="space-y-3 mb-6">
        {visibleComments.length === 0 && (
          <p className="text-sm text-stone-400 italic">
            {t('Be the first to share your thoughts.', '첫 번째 댓글을 남겨보세요.')}
          </p>
        )}
        {visibleComments.map((c) => (
          <div
            key={c.id}
            className={`p-3 rounded-lg border ${c.blocked ? 'border-red-100 bg-red-50/50' : 'border-stone-100 bg-stone-50'}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                {/* Author + meta */}
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-sm font-semibold text-stone-800">{c.author}</span>
                  {c.userId && (
                    <>
                      {isAdmin && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-teal-50 text-teal-600 font-mono font-medium">
                          {c.userId.slice(0, 8)}
                        </span>
                      )}
                      {!isAdmin && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-stone-200 text-stone-500 font-medium">
                          {t('Member', '회원')}
                        </span>
                      )}
                    </>
                  )}
                  {!c.userId && isAdmin && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-50 text-orange-500 font-medium">
                      {t('Anonymous', '익명')}
                    </span>
                  )}
                  {c.blocked && (
                    <span className="text-xs text-red-500 font-medium">[{t('Blocked', '차단됨')}]</span>
                  )}
                  <span className="text-xs text-stone-400">{c.date}</span>
                </div>

                {/* Text or edit mode */}
                {editingId === c.id ? (
                  <div className="space-y-2 mt-1">
                    <textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      maxLength={300}
                      rows={2}
                      className="w-full text-sm px-3 py-2 border border-stone-200 rounded-lg bg-white focus:outline-none focus:border-stone-400 resize-none"
                    />
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-stone-400">{editText.length}/300</span>
                      <div className="flex gap-2">
                        <button
                          onClick={cancelEdit}
                          className="text-xs px-3 py-1 rounded-lg border border-stone-200 text-stone-600 hover:bg-stone-50 cursor-pointer transition-colors whitespace-nowrap"
                        >
                          {t('Cancel', '취소')}
                        </button>
                        <button
                          onClick={() => saveEdit(c.id)}
                          className="text-xs px-3 py-1 rounded-lg bg-stone-800 text-white hover:bg-stone-700 cursor-pointer transition-colors whitespace-nowrap"
                        >
                          {t('Save', '저장')}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-stone-600 leading-relaxed">{c.text}</p>
                )}
              </div>

              {/* Action buttons */}
              <div className="flex items-center gap-1 shrink-0">
                {/* Admin: block + delete */}
                {isAdmin && (
                  <>
                    {c.blocked ? (
                      <button
                        onClick={() => handleBlock(c.id, c.blocked)}
                        title={t('Unblock comment', '댓글 차단 해제')}
                        className="w-7 h-7 flex items-center justify-center text-teal-500 hover:bg-teal-50 rounded-full cursor-pointer transition-colors"
                      >
                        <i className="ri-eye-line text-xs" />
                      </button>
                    ) : (
                      <button
                        onClick={() => handleBlock(c.id, c.blocked)}
                        title={t('Block comment', '댓글 차단')}
                        className="w-7 h-7 flex items-center justify-center text-amber-500 hover:bg-amber-50 rounded-full cursor-pointer transition-colors"
                      >
                        <i className="ri-eye-off-line text-xs" />
                      </button>
                    )}
                    <button
                      onClick={() => setDeleteTarget(c.id)}
                      title={t('Delete comment', '댓글 삭제')}
                      className="w-7 h-7 flex items-center justify-center text-red-400 hover:bg-red-50 rounded-full cursor-pointer transition-colors"
                    >
                      <i className="ri-delete-bin-line text-xs" />
                    </button>
                  </>
                )}

                {/* Logged-in owner: edit + delete (not admin) */}
                {!isAdmin && canModifyComment(c) && (
                  <>
                    <button
                      onClick={() => startEdit(c)}
                      title={t('Edit comment', '댓글 수정')}
                      className="w-7 h-7 flex items-center justify-center text-stone-400 hover:bg-stone-100 rounded-full cursor-pointer transition-colors"
                    >
                      <i className="ri-edit-line text-xs" />
                    </button>
                    <button
                      onClick={() => setDeleteTarget(c.id)}
                      title={t('Delete comment', '댓글 삭제')}
                      className="w-7 h-7 flex items-center justify-center text-red-400 hover:bg-red-50 rounded-full cursor-pointer transition-colors"
                    >
                      <i className="ri-delete-bin-line text-xs" />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <DeleteConfirmModal
          onConfirm={() => handleDelete(deleteTarget)}
          onCancel={() => setDeleteTarget(null)}
          t={t}
        />
      )}

      {/* Comment form */}
      {commentsEnabled ? (
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="flex gap-2 flex-col sm:flex-row">
            {isLoggedIn ? (
              <div className="shrink-0 text-sm px-3 py-2 rounded-lg bg-stone-100 text-stone-600 border border-stone-200 whitespace-nowrap">
                <span className="font-medium text-stone-700">
                  {user.displayName || user.email.split('@')[0]}
                </span>
                <span className="text-xs text-stone-400 ml-1">({t('You', '나')})</span>
              </div>
            ) : (
              <input
                type="text"
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                placeholder={t('Your name', '이름')}
                maxLength={50}
                className="shrink-0 w-full sm:w-28 text-sm px-3 py-2 border border-stone-200 rounded-lg bg-white focus:outline-none focus:border-stone-400 placeholder-stone-300"
              />
            )}
            <input
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t('Share your thoughts...', '생각을 남겨보세요...')}
              maxLength={300}
              className="flex-1 text-sm px-3 py-2 border border-stone-200 rounded-lg bg-white focus:outline-none focus:border-stone-400 placeholder-stone-300"
            />
            <button
              type="submit"
              disabled={submitting}
              className="shrink-0 text-sm px-4 py-2 bg-stone-800 text-white rounded-lg hover:bg-stone-700 cursor-pointer transition-colors whitespace-nowrap disabled:opacity-50"
            >
              {submitting ? <i className="ri-loader-4-line animate-spin" /> : t('Post', '등록')}
            </button>
          </div>
          {error && <p className="text-xs text-red-500">{error}</p>}
          {submitted && <p className="text-xs text-teal-600">{t('Comment posted!', '댓글이 등록되었습니다!')}</p>}
          <p className="text-xs text-stone-400">{text.length}/300</p>
        </form>
      ) : (
        <div className="flex items-center gap-2 px-4 py-3 bg-stone-50 rounded-xl border border-stone-100">
          <i className="ri-chat-off-line text-stone-400 text-sm" />
          <p className="text-xs text-stone-400">
            {t('Comments are disabled for this post.', '이 게시물은 댓글이 비활성화되어 있습니다.')}
          </p>
        </div>
      )}
    </div>
  );
};

export default CommentSection;